import type { ReactNode } from 'react';

import { Button, CircularProgress, Typography } from '@mui/material';
import { clsx } from 'clsx';
import remarkGfm from 'remark-gfm';

import { DynamicReactMarkdown } from 'components/DynamicReactMarkdown';
import { CHAT_TEXT_CLASS, chatMarkdownComponents } from 'features/chat/ui/markdownComponents';
import type { Option } from 'features/onboarding/lib/conversation';

export function TruvAvatar() {
  return <img src="/favicon.svg" alt="" aria-hidden className="h-7 w-7 shrink-0 rounded-lg" />;
}

/**
 * One turn in the thread: the assistant's on the left behind the avatar, the user's on the right.
 *
 * The shape is shared so the two can't drift apart — only the colours and which side it sits on
 * differ, and a bubble that picked up its own padding or corner radius would read as a third kind.
 */
function Bubble({ from, children }: { from: 'assistant' | 'user'; children: ReactNode }) {
  const isUser = from === 'user';

  return (
    <div className={clsx('flex gap-2', isUser ? 'justify-end' : 'items-start')}>
      {!isUser && <TruvAvatar />}
      <div
        className={clsx(
          'max-w-[85%] rounded-2xl px-4 py-2.5',
          CHAT_TEXT_CLASS,
          isUser ? 'bg-blue text-white' : 'bg-grey20 text-grey90',
        )}
      >
        {children}
      </div>
    </div>
  );
}

export function SystemBubble({ children }: { children: ReactNode }) {
  return <Bubble from="assistant">{children}</Bubble>;
}

export function UserBubble({ text }: { text: string }) {
  return <Bubble from="user">{text}</Bubble>;
}

// Assistant reply, streamed from /v1/chat/. Renders markdown; shows a spinner until the first token.
export function AiBubble({ text }: { text: string }) {
  return (
    <Bubble from="assistant">
      {text ? (
        <DynamicReactMarkdown components={chatMarkdownComponents} remarkPlugins={[remarkGfm]}>
          {text}
        </DynamicReactMarkdown>
      ) : (
        <CircularProgress size={14} />
      )}
    </Bubble>
  );
}

function OptionCardButton({ option, selected, onClick }: { option: Option; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        'w-full cursor-pointer rounded-xl border p-3 text-left transition-colors',
        selected ? 'border-blue bg-white' : 'border-grey30 bg-white hover:border-grey40',
      )}
    >
      <Typography className="font-medium text-grey90">{option.title}</Typography>
      {option.description && (
        <Typography variant="body2" className="mt-0.5 text-grey70">
          {option.description}
        </Typography>
      )}
    </button>
  );
}

export function MultiOptionCards({
  prompt,
  options,
  selected,
  onToggle,
  onConfirm,
  disabled,
}: {
  prompt: string;
  options: Option[];
  selected: string[];
  onToggle: (id: string) => void;
  onConfirm: () => void;
  disabled: boolean;
}) {
  return (
    <SystemBubble>
      <div className="mb-2">{prompt}</div>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <OptionCardButton
            key={option.id}
            option={option}
            selected={selected.includes(option.id)}
            onClick={() => !disabled && onToggle(option.id)}
          />
        ))}
      </div>
      {!disabled && (
        <Button variant="contained" size="small" sx={{ mt: 1.5 }} onClick={onConfirm}>
          Continue
        </Button>
      )}
    </SystemBubble>
  );
}
